import { AlertTriangle, Calendar, Clock, ExternalLink, User } from "lucide-react";
import { DateTime } from "luxon";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface DayDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: DateTime;
  shifts: any[];
  onNavigateToDay?: (date: DateTime) => void;
}

interface ShiftBlockProps {
  shift: any;
  worker?: any;
}

function ShiftBlock({ shift, worker }: ShiftBlockProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "SCHEDULED":
        return "bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300";
      case "CONFIRMED":
        return "bg-green-100 border-green-300 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300";
      case "IN_PROGRESS":
        return "bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-300";
      case "COMPLETED":
        return "bg-gray-100 border-gray-300 text-gray-800 dark:bg-gray-900/20 dark:border-gray-700 dark:text-gray-300";
      case "CANCELLED":
        return "bg-red-100 border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300";
      default:
        return "bg-gray-100 border-gray-300 text-gray-800 dark:bg-gray-900/20 dark:border-gray-700 dark:text-gray-300";
    }
  };

  return (
    <Card className={cn("border-l-4", getStatusColor(shift.status))}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className="font-medium">
              {shift.startTime} - {shift.endTime}
            </span>
          </div>
          <Badge variant="outline" className={getStatusColor(shift.status)}>
            {shift.status.toLowerCase().replace("_", " ")}
          </Badge>
        </div>

        {worker && (
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4" />
            <span className="text-sm">
              {`${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim() ||
                "Unknown Worker"}
            </span>
          </div>
        )}

        <div className="space-y-1">
          <div className="text-sm text-muted-foreground">
            Type: {shift.shiftType || "Standard"}
            {shift.isException && (
              <Badge variant="outline" className="ml-2 h-5">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Exception
              </Badge>
            )}
          </div>

          {shift.notes && <div className="text-sm text-muted-foreground">Notes: {shift.notes}</div>}

          {shift.breaks && shift.breaks.length > 0 && (
            <div className="text-xs text-muted-foreground">
              Breaks: {shift.breaks.length} scheduled
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CoverageSummary({ shifts }: { shifts: any[] }) {
  // Calculate coverage by hour
  const hourlyStats = React.useMemo(() => {
    const stats = new Map<number, number>();

    shifts.forEach((shift) => {
      const [startHour] = shift.startTime.split(":").map(Number);
      const [endHour] = shift.endTime.split(":").map(Number);

      for (let hour = startHour; hour < endHour; hour++) {
        stats.set(hour, (stats.get(hour) || 0) + 1);
      }
    });

    return stats;
  }, [shifts]);

  const businessHours = Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 8 PM
  const _maxWorkers = Math.max(...Array.from(hourlyStats.values()), 1);

  const getCoverageColor = (workerCount: number): string => {
    if (workerCount === 0) return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300";
    if (workerCount === 1)
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300";
    if (workerCount === 2)
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300";
    return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300";
  };

  const _getCoverageLabel = (workerCount: number): string => {
    if (workerCount === 0) return "No coverage";
    if (workerCount === 1) return "Light coverage";
    if (workerCount === 2) return "Good coverage";
    return "Full coverage";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Hourly Coverage</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-2">
          {businessHours.map((hour) => {
            const workerCount = hourlyStats.get(hour) || 0;
            return (
              <div
                key={hour}
                className={cn(
                  "p-2 rounded text-center text-xs font-medium",
                  getCoverageColor(workerCount)
                )}
              >
                <div className="font-semibold">{hour}:00</div>
                <div className="text-xs mt-1">
                  {workerCount} worker{workerCount !== 1 ? "s" : ""}
                </div>
              </div>
            );
          })}
        </div>

        {/* Coverage alerts */}
        <div className="mt-4 space-y-2">
          {Array.from(hourlyStats.entries()).filter(([_, count]) => count === 0).length > 0 && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span>
                {Array.from(hourlyStats.entries()).filter(([_, count]) => count === 0).length} hours
                with no coverage
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function DayDetailModal({
  isOpen,
  onClose,
  date,
  shifts,
  onNavigateToDay,
}: DayDetailModalProps) {
  const totalShifts = shifts.length;
  const earliestShift = shifts.reduce(
    (earliest, shift) => (shift.startTime < earliest.startTime ? shift : earliest),
    shifts[0] || { startTime: "09:00" }
  );

  const latestShift = shifts.reduce(
    (latest, shift) => (shift.endTime > latest.endTime ? shift : latest),
    shifts[0] || { endTime: "17:00" }
  );

  const uniqueWorkers = new Set(shifts.map((shift) => shift.workerSalonId)).size;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{date.toFormat("EEEE, MMMM d, yyyy")}</span>
            </div>
            {onNavigateToDay && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigateToDay(date)}
                className="ml-4"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Daily View
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {shifts.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No shifts scheduled</h3>
            <p className="text-muted-foreground">There are no shifts scheduled for this day.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Day summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{totalShifts}</div>
                  <div className="text-sm text-muted-foreground">Total Shifts</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{uniqueWorkers}</div>
                  <div className="text-sm text-muted-foreground">Workers</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{earliestShift?.startTime || "--"}</div>
                  <div className="text-sm text-muted-foreground">First Shift</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{latestShift?.endTime || "--"}</div>
                  <div className="text-sm text-muted-foreground">Last Shift</div>
                </CardContent>
              </Card>
            </div>

            {/* Coverage analysis */}
            <CoverageSummary shifts={shifts} />

            {/* Shifts list */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Scheduled Shifts</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {shifts
                  .sort((a, b) => a.startTime.localeCompare(b.startTime))
                  .map((shift) => (
                    <ShiftBlock
                      key={shift.id}
                      shift={shift}
                      // Note: Worker data would need to be fetched/passed in
                      worker={{ user: { firstName: "Worker", lastName: shift.workerSalonId } }}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
